// ════════════════════════════════════════════════════════════════════════════
//  ManDana — tags
//  ---------------------------------------------------------------------------
//  Tags live in the writing as #names. The index is derived from that source,
//  never stored beside it, so sync and undo cannot leave prose and metadata in
//  disagreement. Identity is the Unicode-normalised, case-folded name; display
//  keeps the spelling the writer first used.
//
//  The grammar follows the useful common ground of mature knowledge tools:
//    • letters from every script, combining marks, numbers, _ and -;
//    • hierarchy with /, such as #work/research;
//    • every segment begins with a letter or _, which refuses #2026 and makes a
//      bare colour/issue number unambiguous;
//    • a tag cannot begin in a word, URL fragment or escaped hash.
// ════════════════════════════════════════════════════════════════════════════

interface MdTagToken {
  start: number;
  end: number;
  raw: string;
  name: string;
  key: string;
}

(function (g: any) {
  'use strict';

  const SEGMENT = '[\\p{L}\\p{M}_][\\p{L}\\p{M}\\p{N}_-]*';
  const TAG_RE = new RegExp('#(' + SEGMENT + '(?:/' + SEGMENT + ')*)', 'gu');
  const NAME_RE = new RegExp('^' + SEGMENT + '(?:/' + SEGMENT + ')*$', 'u');
  const WORD_RE = /[\p{L}\p{M}\p{N}_]/u;
  const MAX_NAME = 80;

  function fold(value: string): string {
    return value.normalize('NFKC').toLocaleLowerCase();
  }

  /** A clean display name without its hash, or null when it is not a tag. */
  function mdTagName(value: unknown): string | null {
    if (typeof value !== 'string') return null;
    const clean = value.trim().replace(/^#/, '').normalize('NFKC').slice(0, MAX_NAME);
    return clean && NAME_RE.test(clean) ? clean : null;
  }

  function mdTagKey(value: unknown): string {
    const name = mdTagName(value);
    return name ? fold(name) : '';
  }

  function allowedAt(text: string, start: number): boolean {
    if (start === 0) return true;
    const prev = text[start - 1];
    return prev !== '\\' && prev !== '/' && !WORD_RE.test(prev || '');
  }

  /** Every valid occurrence, in source order. */
  function mdTagTokens(value: unknown): MdTagToken[] {
    if (typeof value !== 'string' || !value) return [];
    const out: MdTagToken[] = [];
    TAG_RE.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = TAG_RE.exec(value))) {
      if (!allowedAt(value, match.index)) continue;
      const name = mdTagName(match[1]);
      if (!name) continue;
      out.push({ start: match.index, end: match.index + match[0].length,
        raw: match[0], name, key: fold(name) });
    }
    return out;
  }

  /** Unique tags in first-use order. */
  function mdTags(value: unknown): Array<{ name: string; key: string }> {
    const seen = new Set<string>();
    return mdTagTokens(value).flatMap((tag) => {
      if (seen.has(tag.key)) return [];
      seen.add(tag.key);
      return [{ name: tag.name, key: tag.key }];
    });
  }

  /** The unfinished tag immediately behind a caret, used by autocomplete. */
  function mdTagFragment(value: unknown, caret: unknown): { start: number; end: number; query: string } | null {
    if (typeof value !== 'string' || !Number.isInteger(caret)) return null;
    const end = Math.max(0, Math.min(value.length, caret as number));
    const line = value.lastIndexOf('\n', end - 1) + 1;
    const hash = value.lastIndexOf('#', end - 1);
    if (hash < line || !allowedAt(value, hash)) return null;
    const query = value.slice(hash + 1, end);
    if (query.length > MAX_NAME || /\s/.test(query)) return null;
    if (query && !new RegExp('^' + SEGMENT + '(?:/' + SEGMENT + ')*(?:/?)$', 'u').test(query)) return null;
    return { start: hash, end, query };
  }

  /** Replace an autocomplete fragment with one canonical occurrence. */
  function mdTagComplete(value: unknown, start: unknown, end: unknown, nameValue: unknown):
    { text: string; caret: number; name: string; key: string } | null {
    if (typeof value !== 'string' || !Number.isInteger(start) || !Number.isInteger(end)) return null;
    const name = mdTagName(nameValue);
    const a = start as number, b = end as number;
    if (!name || a < 0 || b < a || b > value.length || value[a] !== '#') return null;
    const insert = '#' + name;
    return { text: value.slice(0, a) + insert + value.slice(b), caret: a + insert.length,
      name, key: fold(name) };
  }

  /** Add from the inspector. A final tag row stays one quiet metadata row. */
  function mdTagAdd(value: unknown, nameValue: unknown): { text: string; caret: number } | null {
    const text = typeof value === 'string' ? value : '';
    const name = mdTagName(nameValue);
    if (!name) return null;
    const key = fold(name);
    if (mdTags(text).some((tag) => tag.key === key)) return { text, caret: text.length };
    const lines = text.split('\n');
    const last = lines[lines.length - 1] || '';
    const onlyTags = last.trim() && mdTagTokens(last).reduce((rest, tag) =>
      rest.slice(0, tag.start) + ' '.repeat(tag.end - tag.start) + rest.slice(tag.end), last).trim() === '';
    const spacer = !text ? '' : onlyTags ? (text.endsWith(' ') ? '' : ' ') : (text.endsWith('\n') ? '\n' : '\n\n');
    const next = text + spacer + '#' + name;
    return { text: next, caret: next.length };
  }

  /** Remove every occurrence of one tag without rewriting the surrounding prose. */
  function mdTagRemove(value: unknown, tagValue: unknown): { text: string; caret: number } | null {
    if (typeof value !== 'string') return null;
    const key = mdTagKey(tagValue);
    if (!key) return null;
    let text = value;
    const matches = mdTagTokens(text).filter((tag) => tag.key === key).reverse();
    for (const tag of matches) {
      let a = tag.start, b = tag.end;
      if (a > 0 && text[a - 1] === ' ' && (b === text.length || /[\s.,;:!?)]/.test(text[b] || ''))) a--;
      else if (b < text.length && text[b] === ' ') b++;
      text = text.slice(0, a) + text.slice(b);
    }
    text = text.replace(/\n[ \t]+\n/g, '\n\n').replace(/\s+$/g, '');
    return { text, caret: text.length };
  }

  g.mdTagName = mdTagName;
  g.mdTagKey = mdTagKey;
  g.mdTagTokens = mdTagTokens;
  g.mdTags = mdTags;
  g.mdTagFragment = mdTagFragment;
  g.mdTagComplete = mdTagComplete;
  g.mdTagAdd = mdTagAdd;
  g.mdTagRemove = mdTagRemove;

  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) mod.exports = {
    mdTagName, mdTagKey, mdTagTokens, mdTags, mdTagFragment, mdTagComplete, mdTagAdd, mdTagRemove,
  };
})(typeof window !== 'undefined' ? window : globalThis);
